/**
 * Timeline List Component
 *
 * Displays event timeline entries with support for both admin (editable) and crew (read-only) views.
 * Auto-generated entries (Crew Call, Start Time, End Time) are highlighted.
 */

import { type TimelineEntry } from '../../api/events';
import { formatTime } from '../../utils/dateFormatting';
import { isAutoGeneratedEntry } from '../../utils/timeline';

interface TimelineListProps {
  entries: TimelineEntry[];
  /** Show "Auto" badge for auto-generated entries (admin view) */
  showAutoBadge?: boolean;
  /** Enable edit button for non-auto entries */
  editable?: boolean;
  /** Called when edit button is clicked */
  onEdit?: (entry: TimelineEntry) => void;
  /** Empty state message */
  emptyMessage?: string;
}

export default function TimelineList({
  entries,
  showAutoBadge = false,
  editable = false,
  onEdit,
  emptyMessage = 'No timeline entries yet',
}: TimelineListProps) {
  if (!entries || entries.length === 0) {
    return <p className="text-sm text-gray-500">{emptyMessage}</p>;
  }

  // Sort entries by time
  const sortedEntries = [...entries].sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
  );

  return (
    <div className="space-y-3">
      {sortedEntries.map((entry) => {
        const isAuto = isAutoGeneratedEntry(entry.id);

        return (
          <div
            key={entry.id}
            className={`flex items-start gap-4 py-2 border-b border-gray-100 last:border-0 group ${
              isAuto ? 'bg-primary-50 -mx-2 px-2 rounded-md border-b-0' : ''
            }`}
          >
            {/* Time */}
            <span
              className={`text-sm font-medium w-20 flex-shrink-0 ${
                isAuto ? 'text-primary-700' : 'text-primary-600'
              }`}
            >
              {formatTime(entry.time)}
            </span>

            {/* Content */}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p
                  className={`text-sm font-medium ${
                    isAuto ? 'text-primary-900' : 'text-gray-900'
                  }`}
                >
                  {entry.label}
                </p>
                {showAutoBadge && isAuto && (
                  <span className="text-xs text-primary-600 bg-primary-100 px-1.5 py-0.5 rounded">
                    Auto
                  </span>
                )}
              </div>
              {entry.description && (
                <p className="text-sm text-gray-500 mt-0.5">{entry.description}</p>
              )}
            </div>

            {/* Edit button (only for non-auto entries in editable mode) */}
            {editable && !isAuto && onEdit && (
              <button
                onClick={() => onEdit(entry)}
                className="text-gray-400 hover:text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Edit entry"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
